/**
 * commissions.ts — Revenus de commission Lil & Co (0,99 €/commande), PURS & testables.
 * Sépare ENCAISSÉ (Stripe, déjà reçu) et À RECOUVRER (réglé sur place → à facturer).
 * Données : RPC `network_commissions` (compte par resto, déjà filtré selon les règles).
 */

export const COMMISSION_CENTS = 99; // 0,99 € / commande passée via le site

/** Ligne brute renvoyée par la RPC (counts par resto). */
export interface TenantCommissionRaw {
  slug: string;
  name: string;
  encaisse: number;  // commandes payées en ligne (Stripe)
  recouvrer: number; // commandes réglées sur place (à facturer)
}

export interface TenantCommission {
  slug: string;
  name: string;
  encaisseOrders: number;
  recouvrerOrders: number;
  encaisseCents: number;
  recouvrerCents: number;
  totalCents: number;
}

export interface CommissionTotals {
  encaisseOrders: number;
  recouvrerOrders: number;
  totalOrders: number;
  encaisseCents: number;
  recouvrerCents: number;
  totalCents: number;
}

function n(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Met en forme une ligne resto (counts → € encaissé / à recouvrer / total). */
export function shapeTenantCommission(raw: TenantCommissionRaw): TenantCommission {
  const enc = Math.max(0, Math.trunc(n(raw.encaisse)));
  const rec = Math.max(0, Math.trunc(n(raw.recouvrer)));
  return {
    slug: raw.slug,
    name: raw.name,
    encaisseOrders: enc,
    recouvrerOrders: rec,
    encaisseCents: enc * COMMISSION_CENTS,
    recouvrerCents: rec * COMMISSION_CENTS,
    totalCents: (enc + rec) * COMMISSION_CENTS,
  };
}

/** Liste par resto (triée par revenu total décroissant, restos sans commande exclus). */
export function tenantCommissions(rows: TenantCommissionRaw[]): TenantCommission[] {
  return (rows ?? [])
    .map(shapeTenantCommission)
    .filter((t) => t.encaisseOrders + t.recouvrerOrders > 0)
    .sort((a, b) => b.totalCents - a.totalCents);
}

/** Totaux réseau (mes revenus) sur la période. */
export function commissionTotals(rows: TenantCommissionRaw[]): CommissionTotals {
  return (rows ?? []).reduce<CommissionTotals>(
    (acc, r) => {
      const enc = Math.max(0, Math.trunc(n(r.encaisse)));
      const rec = Math.max(0, Math.trunc(n(r.recouvrer)));
      acc.encaisseOrders += enc;
      acc.recouvrerOrders += rec;
      acc.totalOrders += enc + rec;
      acc.encaisseCents += enc * COMMISSION_CENTS;
      acc.recouvrerCents += rec * COMMISSION_CENTS;
      acc.totalCents += (enc + rec) * COMMISSION_CENTS;
      return acc;
    },
    { encaisseOrders: 0, recouvrerOrders: 0, totalOrders: 0, encaisseCents: 0, recouvrerCents: 0, totalCents: 0 },
  );
}
